import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Typography,
  Button,
  Paper,
  Collapse,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { maxLengthValidation } from '../../utils/validations.util';

const TranslationEditor = memo(
  ({
    languages,
    selectedLanguage,
    control,
    errors,
    rowData,
    translationFields,
  }) => {
    const language = languages.find((l) => l.value === selectedLanguage);
    const [expandedFields, setExpandedFields] = useState({});

    const toggleFieldExpanded = (fieldKey) => {
      setExpandedFields((prev) => ({
        ...prev,
        [fieldKey]: !prev[fieldKey],
      }));
    };

    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 2,
            pb: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">{language.label} Translation</Typography>
            <Typography variant="body2" color="text.secondary">
              Translate or edit fields for this language.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Text Content
          </Typography>

          {translationFields.map((field) => {
            const fieldName = `${field.key}_${selectedLanguage}`;
            const originalValue = rowData ? rowData[field.key] || '' : '';
            const isExpanded = !!expandedFields[field.key];

            return (
              <Paper key={field.key} variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    {field.label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {originalValue && (
                      <Button
                        onClick={() => toggleFieldExpanded(field.key)}
                        size="small"
                        variant="text"
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        {isExpanded ? 'Hide English' : 'Show English'}
                      </Button>
                    )}
                  </Box>
                </Box>

                {originalValue && (
                  <Collapse in={isExpanded}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        bgcolor: 'action.hover',
                        borderLeft: 3,
                        borderColor: 'primary.main',
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {originalValue}
                      </Typography>
                    </Paper>
                  </Collapse>
                )}

                <Controller
                  name={fieldName}
                  control={control}
                  rules={{
                    ...{
                      ...maxLengthValidation(
                        field.key === 'title' ? 255 : 2500
                      ),
                    },
                  }}
                  render={({ field: controllerField }) => (
                    <TextField
                      {...controllerField}
                      label={field.label}
                      type="text"
                      error={!!errors[fieldName]}
                      helperText={errors[fieldName]?.message || null}
                      fullWidth
                      multiline={field.type !== 'text'}
                      rows={field.type === 'text' ? 1 : 3}
                    />
                  )}
                />
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  }
);

TranslationEditor.propTypes = {
  languages: PropTypes.array.isRequired,
  selectedLanguage: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  rowData: PropTypes.object.isRequired,
  translationFields: PropTypes.array.isRequired,
};

export default TranslationEditor;
